// components/partial/DepositModal.jsx
import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { HTTPService } from '../../services/httpService';
import { isActiveDepositRow } from '../../util/depositUtils';

const API = new HTTPService();

class DepositModal extends Component {
  state = {
    depositAmount: 0,
    depositStatus: 'loading',
    depositPayments: [],
    creatingInvoice: false
  };

  async componentDidMount() {
    if (this.props.show && this.props.request) {
      await this.loadDepositInfo();
    }
  }

  async componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show && this.props.request) {
      await this.loadDepositInfo();
    }
  }

  loadDepositInfo = async () => {
    const { request } = this.props;
    
    // Set current deposit amount
    this.setState({ depositAmount: request.depositAmount || 0 });
    
    // Load payment status. Voided deposits are excluded throughout — a shop that voids a
    // deposit invoice must be able to issue a replacement.
    try {
      const { data: payments } = await API.get(`/installerPaymentsRequest/${request.requestId}`);
      const deposits = payments.filter(isActiveDepositRow);

      let status = 'none';
      if (deposits.length > 0) {
        if (deposits.some(d => d.payment_state === 'paid')) status = 'paid';
        // 'draft' is what the invoice.created webhook writes: the Stripe invoice exists
        // but has not been finalized, so the customer has NOT been emailed yet.
        else if (deposits.every(d => d.payment_state === 'draft')) status = 'pending';
        else status = 'invoiced';
      }

      this.setState({
        depositPayments: deposits,
        depositStatus: status
      });
    } catch (error) {
      console.error('Error loading deposit info:', error);
      this.setState({ depositStatus: 'error' });
    }
  };

  handleAmountChange = (e) => {
    const amount = parseFloat(e.target.value) || 0;
    const maxAmount = this.props.request?.requestTotal || 0;
    
    // Prevent amount greater than job total
    if (amount <= maxAmount) {
      this.setState({ depositAmount: amount });
    }
  };

  handleSaveAmount = async () => {
  // Instance flag, not state: setState is async, so a fast double-click can slip past a
  // state-based check. `creatingInvoice` state exists only to drive the button.
  if (this.saving) return;
  this.saving = true;
  this.setState({ creatingInvoice: true });

  try {
    // Always write the amount first.
    await this.props.onSaveDeposit(this.props.request, this.state.depositAmount);

    // Post-booking (scheduled/received): the parent passes onCreateDepositInvoice, so we
    // create + send the Stripe deposit invoice now — booking has already passed and would
    // never create it. The parent re-checks for an existing deposit against fresh server
    // data and throws DEPOSIT_EXISTS, so this can't mint a second live invoice even if the
    // state we loaded is stale.
    if (this.props.onCreateDepositInvoice && this.state.depositAmount > 0) {
      let result;
      try {
        result = await this.props.onCreateDepositInvoice({
          ...this.props.request,
          depositAmount: this.state.depositAmount,
          deposit_amount: this.state.depositAmount,
        });
      } catch (createError) {
        this.props.onHide();
        alert(createError.code === 'DEPOSIT_EXISTS'
          ? 'A deposit invoice already exists for this job.'
          : `The deposit invoice was not created:\n\n${createError.message}`);
        return;
      }

      this.props.onHide();
      // Deliberately not claiming the customer was emailed. `auto_advance` finalizes
      // asynchronously and has been observed leaving deposit invoices stuck in draft
      // (requests 7386 and 7506), in which case nothing was sent. Never render the hosted
      // invoice URL from this response either — it is null on drafts.
      const collected = result && result.subtotal != null
        ? ` The customer will be asked for $${this.state.depositAmount.toFixed(2)}.`
        : '';
      alert(`Deposit invoice created for this job.${collected}`);
      return;
    }

    this.props.onHide();

    // Pre-booking (new): the invoice is created at booking, not now.
    if (this.state.depositAmount > 0) {
      alert(`Deposit amount set to $${this.state.depositAmount.toFixed(2)}. Invoice will be created when you book the job.`);
    }

  } catch (error) {
    alert('Error saving deposit');
    console.error('Error saving deposit:', error);
  } finally {
    this.saving = false;
    this.setState({ creatingInvoice: false });
  }

};

  handleResend = async () => {
    const deposit = this.state.depositPayments[0];
    if (!deposit) return;
    try {
      await this.props.onResendDepositInvoice(deposit);
      this.props.onHide();
    } catch (error) {
      alert('Error resending deposit invoice');
      console.error('Error resending deposit:', error);
    }
  };

  handleRemoveDeposit = () => {
    if (this.state.depositPayments.length > 0) {
      alert('Cannot remove deposit - invoices already created. Contact support if needed.');
      return;
    }
    
    if (window.confirm('Remove deposit requirement from this job?')) {
      this.setState({ depositAmount: 0 });
    }
  };

  render() {
    const { show, onHide, request } = this.props;
    const { depositAmount, depositStatus, depositPayments, creatingInvoice } = this.state;

    if (!request) return null;

    const totalPaid = depositPayments
      .filter(p => p.payment_state === 'paid')
      .reduce((sum, p) => sum + p.payment_amount, 0);

    const hasInvoices = depositPayments.length > 0;
    const isPaid = depositStatus === 'paid';
    // Post-booking (scheduled/received) instances get onCreateDepositInvoice, so their
    // "save" also creates + sends the invoice. When an unpaid invoice already exists,
    // the primary action becomes Resend instead of Create (never mint a duplicate).
    const isPostBooking = !!this.props.onCreateDepositInvoice;
    const canResend = hasInvoices && !isPaid && !!this.props.onResendDepositInvoice;
    const saveLabel = isPostBooking ? 'Create & Send Invoice' : 'Save Deposit Amount';

    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Deposit Value
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          <div className="job-info mb-3 p-3 bg-light rounded">
            <h6 className="mb-1">{request.vehicleYear} {request.vehicleMake} {request.vehicleModel}</h6>
            <p className="text-muted mb-1">{request.serviceRequested}</p>
            <p className="mb-0"><strong>Job Total: ${request.requestTotal?.toFixed(2)}</strong></p>
          </div>

          {/* Deposit Amount Input */}
          <div className="form-group">
            <label htmlFor="depositAmount">
              <strong>Deposit Amount</strong>
              {depositAmount > 0 && (
                <span className="ml-2">
                  ({((depositAmount / request.requestTotal) * 100).toFixed(0)}% of job)
                </span>
              )}
            </label>
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">$</span>
              </div>
              <input
                type="number"
                className="form-control"
                id="depositAmount"
                value={depositAmount || ''}
                onChange={this.handleAmountChange}
                placeholder="0.00"
                min="0"
                max={request.requestTotal}
                step="0.01"
                disabled={hasInvoices}
              />
            </div>
            <small className="form-text text-muted">
              {hasInvoices ? 
                'Cannot change amount - deposit invoices already created' :
                `Enter deposit amount (max: $${request.requestTotal?.toFixed(2)})`
              }
            </small>
            
            {depositAmount > 0 && !hasInvoices && (
              <button 
                className="btn btn-link btn-sm p-0 mt-1 text-danger"
                onClick={this.handleRemoveDeposit}
              >
                Remove deposit amount
              </button>
            )}
          </div>

          {/* Deposit Status Display */}
          {depositStatus === 'invoiced' && (
            <div className="alert alert-info">
              <strong>Status:</strong> Deposit invoice has been sent to customer
            </div>
          )}

          {depositStatus === 'pending' && (
            <div className="alert alert-warning">
              <strong>Status:</strong> Deposit invoice created but not yet finalized by Stripe —
              the customer has not been emailed yet. Use Resend if it doesn't clear shortly.
            </div>
          )}

          {depositStatus === 'paid' && (
            <div className="alert alert-success">
              <strong>Status:</strong> Deposit has been paid (${totalPaid.toFixed(2)})
            </div>
          )}

          {depositStatus === 'error' && (
            <div className="alert alert-warning">
              <strong>Status:</strong> Unable to load deposit information
            </div>
          )}
        </Modal.Body>
        
<Modal.Footer>
  <div className="d-flex justify-content-between w-100">
    <div>
      {depositStatus === 'invoiced' && (
        <div className="text-success">
          <small>Deposit invoice created</small>
        </div>
      )}
      
      {depositStatus === 'paid' && (
        <div className="text-success">
          <small>Deposit has been paid</small>
        </div>
      )}
    </div>
    
    <div>
      <Button variant="secondary" onClick={onHide} className="mr-2">
        Cancel
      </Button>
      {canResend ? (
        <Button variant="primary" onClick={this.handleResend}>
          Resend Invoice
        </Button>
      ) : !hasInvoices ? (
        <Button variant="success" onClick={this.handleSaveAmount} disabled={creatingInvoice}>
          {creatingInvoice ? 'Working…' : saveLabel}
        </Button>
      ) : null}
    </div>
  </div>
</Modal.Footer>
        <style jsx>{`
          .payment-history .table th {
            border-top: none;
            font-size: 0.875rem;
          }
          
          .payment-history .table td {
            font-size: 0.875rem;
            vertical-align: middle;
          }
          
          .job-info {
            border-left: 4px solid var(--color-info);
          }
          
          .alert {
            margin-bottom: 1rem;
          }
          
          .alert:last-child {
            margin-bottom: 0;
          }
        `}</style>
      </Modal>
    );
  }
}

export default DepositModal;